import { AnyType, NgApiClient } from '@centra/centra-ui'
import { SUPPORT_TYPE_PROPERTY_GID } from './consts'
import { TaskFilterConfig } from './models'
import { CategoryTypeIds } from 'store/taskManager/model'

export const CATEGORY_FILTER: TaskFilterConfig = {
  filter: {
    name: 'categoryIds',
    type: 'options',
    source: 'api',
    title: 'Категория',
    filterField: 'name',
    api: {
      entity: 'taskCategory',
      selector: {
        fields: ['id', 'name', 'colorAlias'],
        predicates: [
          {
            field: 'id',
            operator: 'NOT_EQUALS',
            values: [CategoryTypeIds.ai],
          },
        ],
      },
    },
  },
}

export const STATUS_FILTER: TaskFilterConfig = {
  filter: {
    name: 'statusIds',
    type: 'options',
    source: 'api',
    title: 'Cтатус',
    filterField: 'name',
    api: {
      entity: 'taskStatus',
      selector: {
        fields: ['id', 'name', 'colorAlias'],
      },
    },
  },
}

/** Тип обращения */
const getNameTypeFilter = (httpClient: NgApiClient): TaskFilterConfig => ({
  permission: ['role.account-manager', 'role.admin', 'role.supervisor'],
  filter: {
    name: 'supportTypeIds',
    title: 'Тип обращения',
    type: 'options',
    source: 'external',
    getOptions: async () => {
      try {
        /** Здесь нам нужен только idишник свойства `тип обращения`, чтобы корректно составить предикат */
        const result = await httpClient.get<{ id: number }>('property', {
          selector: {
            fields: ['id'],
            predicates: [
              {
                field: 'gid',
                operator: 'IN',
                values: [SUPPORT_TYPE_PROPERTY_GID],
              },
            ],
          },
        })

        const propertyId = result.data.page.entries[0].id
        const optionsResult = await httpClient.get<{ id: number; name: string }>('propertyOption', {
          selector: {
            fields: ['id', 'name'],
            ordering: [{ field: 'id', sortOrder: 'ASC' }],
            predicates: [
              {
                field: 'propertyId',
                operator: 'EQUALS',
                values: [propertyId],
              },
            ],
          },
        })

        return optionsResult.data.page.entries
      } catch (error) {
        return null
      }
    },
  },
})

export const PRODUCT_FILTER: TaskFilterConfig = {
  filter: {
    name: 'productIds',
    title: 'Продукт',
    type: 'options',
    source: 'api',
    api: {
      entity: 'product',
      selector: {
        fields: ['id', 'name', 'newLogo', 'logo', 'status'],
      },
    },
  },
}

export const getAccountManagerClientFilter = (): TaskFilterConfig => ({
  permission: ['role.account-manager', 'role.admin', 'role.supervisor'],
  filter: {
    name: 'clientIds',
    title: 'Клиенты',
    type: 'options',
    source: 'api',
    api: {
      entity: 'client',
      selector: {
        fields: ['id', 'name'],
        predicates: [
          {
            field: 'kind',
            operator: 'IN',
            values: ['advertiser', 'agency'],
          },
        ],
      },
    },
  },
})

const getAccountManagersFilter = (httpClient: NgApiClient): TaskFilterConfig => ({
  permission: ['role.admin', 'role.supervisor', 'role.support'],
  filter: {
    name: 'accountManagerIds',
    title: 'Исполнитель',
    type: 'options',
    filterField: 'accountName',
    source: 'external',
    getBadgeText: async ({ scheme, value, controller }) => {
      const opt = (scheme?.options || (await controller.loadOption(scheme))) as AnyType[]
      const optMap = new Map(opt?.map((v: AnyType) => [v.id, v]))
      const fullOptions: string[] = []

      for (let i = 0; i < value.length; i++) {
        const v = value[i]
        const fullOption = optMap.get(v.id)
        if (fullOption) {
          fullOptions.push(`${fullOption.lastName} ${fullOption.firstName}`)
        }
      }

      return fullOptions
    },
    getOptions: async () => {
      //в рамках https://centrateam.atlassian.net/browse/CA-3618, у нас нет сущности для получения аккаунт менеджеров
      const { data } = await httpClient.restClient.instance
        .request({
          method: 'GET',
          url: '/account/account-managers',
          data: {},
        })
        .then(response => response)
        .catch(error => error.response)

      return data
    },
  },
})

export const CREATED_AT_FILTER: TaskFilterConfig = {
  filter: { name: 'createdFrom', type: 'dateRange', title: 'Период создания' },
}

export const TASK_ID_FILTER: TaskFilterConfig = {
  filter: {
    name: 'taskId',
    title: 'ID задачи',
    type: 'string',
  },
}

export const getTaskFilters = ({ httpClient }: { httpClient: NgApiClient }) => {
  return [
    TASK_ID_FILTER,
    getAccountManagersFilter(httpClient),
    getAccountManagerClientFilter(),
    CATEGORY_FILTER,
    STATUS_FILTER,
    getNameTypeFilter(httpClient),
    PRODUCT_FILTER,
    CREATED_AT_FILTER,
  ]
}
